import React, { useEffect, useState } from 'react';
import { Button, Card, Input, Spacer } from "@heroui/react";
import { Form } from "@heroui/form";
import { fetchAuthSession } from 'aws-amplify/auth';
import { CircularProgress } from "@heroui/react";
import Navbar from '../components/Navbar';
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter
  } from "@heroui/modal";
import { API_URL } from '../api/api';

const Admin: React.FC = () => {
    const [cognitoToken, setCognitoToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [users, setUsers] = useState<any[]>([]); // State to store the users list
    const [modalVisible, setModalVisible] = useState(false); // Modal visibility
    const [selectedUser, setSelectedUser] = useState<any>(null); // User selected for deletion
    const [modalMessage, setModalMessage] = useState<string | null>(null); // Message to show in modal
    const [formData, setFormData] = useState({ email: '' });

    const [, setError] = useState<string | null>(null); // State to track submission error
    const [, setSuccess] = useState(false); // State to track successful submission
    const [showTick, setShowTick] = useState(false); 
    const [, setLoading] = useState(false); // State to track submission status

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);


        const formData = new FormData(e.currentTarget); // Extract form data
        const data = Object.fromEntries(formData.entries());

        try {
            // Fetch the Cognito token and use it directly
            await fetchCognitoToken();
            if (!cognitoToken) {
                throw new Error('Cognito token is not available.');
            }

            const response = await fetch(`${API_URL}/users`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${cognitoToken}`, // Use the token directly
                },
                body: JSON.stringify({'username' : data.email}),
            });
    
            if (!response.ok) {
              throw new Error(`Error: ${response.statusText}`);
            }
      
            const responseData = await response.json();
            console.log('API Response:', responseData);
      
            setSuccess(true);
            setShowTick(true); // Show the green tick

            setFormData({ email: '' });
            // Hide the tick after 3 seconds and navigate to "/"
            setTimeout(() => {
              setShowTick(false);
            }, 3000);

            setSuccess(true);
            fetchUsers(); // Fetch the updated users list
        } catch (err) {
            console.error('Submission Error:', err);
            if (err instanceof Error) {
                setError(err.message || 'An error occurred while submitting the form.');
            } else {
                setError('An error occurred while submitting the form.');
            }
        } finally {
            setLoading(false);
        }
      };

    const fetchCognitoToken = async () => {
        try {
            const session = await fetchAuthSession();
            if (session.tokens?.idToken) {
                setCognitoToken(session.tokens.idToken.toString());
            } else {
                console.log('Tokens are undefined');
            }
        } catch (err) {
            console.log('Error fetching Cognito token:', err);
        }
    };

    const fetchUsers = async () => {
        if (!cognitoToken) {
            console.warn("No Cognito token available. Skipping API call.");
            return;
        }
    
        try {
            const response = await fetch(
                `${API_URL}/users`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${cognitoToken}`,
                    },
                }
            );
    
            if (!response.ok) {
                throw new Error(`Error fetching data: ${response.statusText}`);
            }
    
            const responseData = await response.json();
    
            // Sort users by latest creation date
            const sortedUsers = responseData.users.sort((a: any, b: any) => 
                new Date(b.UserCreateDate).getTime() - new Date(a.UserCreateDate).getTime()
            );
    
            setUsers(sortedUsers); // Save sorted users to state
            console.log(sortedUsers);
        } catch (error) {
            console.error("Error fetching records:", error);
        } finally {
            setIsLoading(false);
        }
    };
    
    
    const onDeleteUserClick = (user: any) => {
        setSelectedUser(user); // Save selected user
        setModalMessage(null); // Reset the modal message
        setModalVisible(true); // Open the modal
    };

    const confirmDeleteUser = async () => {
        if (!selectedUser) return;

        const userId = selectedUser.sub;
        setModalMessage("Deleting user...");
        try {
            const response = await fetch(
                `${API_URL}/users/${userId}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${cognitoToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Error deleting user: ${response.statusText}`);
            }

            // Remove the deleted user from the local state
            setUsers((prevUsers) => prevUsers.filter((user) => user.sub !== userId));
            setModalMessage("User deleted successfully!");
        } catch (error) {
            console.error(`Error deleting user ${userId}:`, error);
            setModalMessage("Failed to delete user. Please try again.");
        }
    };

    useEffect(() => {
        fetchCognitoToken();
    }, []);

    useEffect(() => {
        if (cognitoToken) fetchUsers();
    }, [cognitoToken]);

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {isLoading && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000,
                    }}
                >
                    <CircularProgress label="Loading..." />
                </div>
            )}
            <Navbar />
            <main className="flex items-center justify-center min-h-screen">
                <div className="p-5 w-full max-w-(--breakpoint-sm)">
                    <p className="text-sm font-semibold mb-2 mt-2 text-center">Add a user:</p>
                    <Card>
                        <Form className="flex flex-col gap-4 p-4 items-center w-full" validationBehavior="native" onSubmit={onSubmit}>
                            <Input
                                isRequired
                                className="h-[45px] grow"
                                errorMessage="Please enter a valid email"
                                label="Email"
                                labelPlacement="inside"
                                name="email"
                                type="email"
                                value={formData.email} 
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                            <Button
                                type="submit"
                                color="primary"
                                className="ml-auto"
                            >
                                Add
                            </Button>
                        </Form>
                    </Card>
                    <p className="text-sm font-semibold mt-8 text-center">Users List:</p>
                    <div className="mt-4">
                        {users.map((user) => (
                            <Card key={user.sub} className="p-3 mt-3 flex justify-between items-start">
                                <div>
                                    <p><strong>Email:</strong> {user.email}</p>
                                    <p><strong>Status:</strong> {user.UserStatus}</p>
                                    <p><strong>Created On:</strong> {new Date(user.UserCreateDate).toLocaleString("en-GB")}</p>
                                </div>
                                <Spacer y={3} />
                                <Button
                                    color="danger"
                                    variant="solid"
                                    size="md"
                                    className="ml-auto"
                                    onPress={() => onDeleteUserClick(user)} // Pass user to the function
                                >
                                    Delete
                                </Button>
                            </Card>
                        ))}
                    </div>
                </div>
            </main>
            {showTick && (
                <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent background
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999, // Ensure it overlays everything
                }}
                >
                <div
                    style={{
                    color: 'green',
                    fontSize: '20rem',
                    textAlign: 'center',
                    }}
                >
                    ✓
                </div>
                </div>
            )}
            {/* Modal for Delete Confirmation */}
            <Modal
                closeButton
                aria-labelledby="modal-title"
                isOpen={modalVisible}
                onClose={() => setModalVisible(false)}
            >
            <ModalContent>
                <ModalHeader>
                        Confirm User Deletion
                </ModalHeader>
                <ModalBody>
                    {modalMessage ? (
                        <p>{modalMessage}</p>
                    ) : (
                        <p>Are you sure you want to delete user: {selectedUser?.email}?</p>
                    )}
                </ModalBody>
                {!modalMessage && (
                    <ModalFooter>
                        <Button color="danger" onPress={confirmDeleteUser}>
                            Yes, Delete
                        </Button>
                        <Button onPress={() => setModalVisible(false)}>
                            Cancel
                        </Button>
                    </ModalFooter>
                )}
            </ModalContent>
            </Modal>
        </div>
    );
};

export default Admin;
